/* eslint-disable no-await-in-loop */ import { lazy } from 'react';
import { logger } from 'services';
import { captureChunkErrorTelemetry, isChunkLoadError, isDevelopmentBuild, markAutoReloadForChunkError, markDevFallbackReload, reloadAfterChunkError, retryImportAfterChunkError, shouldAutoReloadForChunkError, shouldDevFallbackReload, shouldLogTerminalChunkError } from 'utils/chunk-error';
/**
 * Wrapper around React.Lazy.
 * Refresh the page when there is a chunk load error
 * Throws error when the error persists even after refresh
 * @param  {} componentImport
 */ export const lazyWithRetry = (componentImport)=>lazy(async ()=>{
        try {
            const component = await componentImport();
            window.localStorage.setItem('page-refreshed', 'false');
            return component;
        } catch (error) {
            var _telemetry_chunkUrl;
            const errorName = error === null || error === void 0 ? void 0 : error.name;
            const errorMessage = error === null || error === void 0 ? void 0 : error.message;
            const messageIsString = typeof errorMessage === 'string';
            // Canonical matcher from utils/chunk-error — do not re-implement inline
            // (two copies drift; the whole PR exists because copies drifted).
            const isChunkError = isChunkLoadError(error);
            if (isChunkError && isDevelopmentBuild()) {
                try {
                    const component = await retryImportAfterChunkError(componentImport);
                    window.localStorage.setItem('page-refreshed', 'false');
                    return component;
                } catch (retryError) {
                    var _telemetry_chunkUrl1;
                    if (shouldDevFallbackReload()) {
                        markDevFallbackReload();
                        reloadAfterChunkError(undefined, {
                            name: retryError === null || retryError === void 0 ? void 0 : retryError.name,
                            message: typeof (retryError === null || retryError === void 0 ? void 0 : retryError.message) === 'string' ? retryError.message : undefined
                        });
                        return new Promise(()=>{});
                    }
                    const telemetry = captureChunkErrorTelemetry(retryError);
                    if (shouldLogTerminalChunkError(String((_telemetry_chunkUrl1 = telemetry.chunkUrl) !== null && _telemetry_chunkUrl1 !== void 0 ? _telemetry_chunkUrl1 : ''), 'lazy')) {
                        logger.warn('ChunkLoadError: dev import retries exhausted — save again after compile finishes', {
                            error: retryError,
                            ...telemetry
                        });
                    }
                    throw retryError;
                }
            }
            // Only chunk-load errors are reload-recoverable. Non-chunk errors
            // (TypeError inside the lazy module, MF remoteEntry network refusal,
            // syntax error in a bundled module, etc.) won't be fixed by a fresh
            // request — and worse, they don't satisfy `isChunkError`, so the
            // sessionStorage debounce marker below is never set. Combined with
            // the fact that the `page-refreshed` localStorage flag is reset to
            // `false` by the very next successful lazy import (line 26 above),
            // a non-chunk failure during boot kicks off an infinite reload
            // cycle: each iteration adds `?__cb=…`, App.tsx's
            // `stripCacheBusterParam` strips it on mount, the same lazy import
            // throws the same error, and we reload again. Visually it looks
            // like the URL "circles back" between the clean path and the
            // cache-buster query.
            if (isChunkError && shouldAutoReloadForChunkError()) {
                // Auto-recovery path: about to reload, ~100% of these resolve on the
                // next page boot. Suppress the Datadog/RUM error log here — the
                // `chunk-error-reload-outcome` LogRocket track event captures the
                // recovery in the next session, and the terminal "already reloaded"
                // branch below still logs the actionable failures. Logging every
                // auto-recovery dominated chunk-error volume without being
                // actionable (PR #17601 follow-up).
                markAutoReloadForChunkError();
                reloadAfterChunkError(undefined, {
                    name: errorName,
                    message: messageIsString ? errorMessage : undefined
                });
                // Keep the lazy promise pending so React stays in Suspense until the
                // browser reload replaces the page. Resolving with undefined here
                // causes React.lazy to read `.default` on undefined and crash.
                return new Promise(()=>{});
            }
            // Terminal path: auto-reload already happened, chunk still failing.
            // This is the actionable signal — log it with the diagnostic
            // telemetry so we can slice failures by CDN / cache / SW state.
            const telemetry = captureChunkErrorTelemetry(error);
            // Dedup chunk failures once per (session, chunkUrl) within this call-site
            // ('lazy' scope) so a repeated import failure logs once. Scoped
            // separately from the boundary fallback ('boundary') so the two distinct
            // signatures ('...giving up' vs '...already reloaded') aren't
            // cross-suppressed. Non-chunk errors are rare and actionable — always log.
            if (!isChunkError || shouldLogTerminalChunkError(String((_telemetry_chunkUrl = telemetry.chunkUrl) !== null && _telemetry_chunkUrl !== void 0 ? _telemetry_chunkUrl : ''), 'lazy')) {
                logger.error(isChunkError ? 'ChunkLoadError: failed to load chunk after reload, giving up' : 'Error occured when trying to load chunk', {
                    error,
                    ...telemetry
                });
            }
            throw error;
        }
    });
const retryForImport = async (s, fn)=>{
    const wait = ()=>new Promise((res)=>setTimeout(res, s * 1000));
    await wait();
    return fn();
};
const exponentRetry = (exponent, fn, retryAfterSecond)=>retryForImport(retryAfterSecond ** exponent, ()=>{
        return fn();
    });
export const exponentialRetryForImport = async (fn, retryTimes = 3, retryAfterSecond = 3)=>{
    let retryCount = 0;
    let retry = true;
    while(retry){
        if (retryCount >= retryTimes) {
            retry = false;
        }
        await exponentRetry(retryCount, fn, retryAfterSecond);
        retryCount += 1;
    }
};
export async function retryImportInsideRoutesDefinition(importFn, retries = 3, delay = 1000) {
    let attempt = 0;
    while(attempt < retries){
        try {
            return await importFn();
        } catch (error) {
            // eslint-disable-next-line no-plusplus
            attempt++;
            if (attempt >= retries) {
                var _telemetry_chunkUrl;
                // Delegate to the shared chunk-error flow (same as lazyWithRetry).
                // The previous raw `window.location.reload()` here had no cache-bust,
                // no debounce, and no terminal state — a persistently-failing chunk
                // put the tab in an INFINITE reload loop, and its log message wasn't
                // an __app_crash signature, so the whole population (~50-90
                // sessions/day, larger than the visible terminal-crash population)
                // was invisible on every crash dashboard.
                const err = error;
                const messageIsString = typeof (err === null || err === void 0 ? void 0 : err.message) === 'string';
                const isChunkError = isChunkLoadError(error);
                if (isChunkError && isDevelopmentBuild()) {
                    try {
                        return await retryImportAfterChunkError(importFn);
                    } catch (retryError) {
                        var _telemetry_chunkUrl1;
                        if (shouldDevFallbackReload()) {
                            markDevFallbackReload();
                            reloadAfterChunkError(undefined, {
                                name: err === null || err === void 0 ? void 0 : err.name,
                                message: messageIsString ? err.message : undefined
                            });
                            return new Promise(()=>{});
                        }
                        const telemetry = captureChunkErrorTelemetry(retryError);
                        if (shouldLogTerminalChunkError(String((_telemetry_chunkUrl1 = telemetry.chunkUrl) !== null && _telemetry_chunkUrl1 !== void 0 ? _telemetry_chunkUrl1 : ''), 'route-retry')) {
                            logger.error('ChunkLoadError: failed to load chunk after reload, giving up', {
                                error: retryError,
                                ...telemetry
                            });
                        }
                        throw retryError;
                    }
                }
                if (isChunkError && shouldAutoReloadForChunkError()) {
                    markAutoReloadForChunkError();
                    reloadAfterChunkError(undefined, {
                        name: err === null || err === void 0 ? void 0 : err.name,
                        message: messageIsString ? err.message : undefined
                    });
                    // Stay pending until the reload replaces the page (see lazyWithRetry).
                    return new Promise(()=>{});
                }
                // Terminal: reload already spent (or non-chunk error, which a reload
                // can't fix). Log with telemetry under the standard signature so it
                // lands on the existing dashboards, then surface to the boundary.
                const telemetry = captureChunkErrorTelemetry(error);
                if (!isChunkError || shouldLogTerminalChunkError(String((_telemetry_chunkUrl = telemetry.chunkUrl) !== null && _telemetry_chunkUrl !== void 0 ? _telemetry_chunkUrl : ''), 'route-retry')) {
                    logger.error(isChunkError ? 'ChunkLoadError: failed to load chunk after reload, giving up' : 'Error occured when trying to load chunk', {
                        error,
                        ...telemetry
                    });
                }
                throw error;
            }
            logger.warn(`Chunk load failed. Retrying... (${attempt}/${retries})`, error);
            // Wait before retrying
            await new Promise((resolve)=>setTimeout(resolve, delay));
        }
    }
    throw new Error('Unreachable code: retry logic should handle failure or success.');
}
